/**
 * The profile form's rules, kept out of the component so they are testable
 * without a DOM — the same split `topics.ts` makes for session dispatch.
 */

import { t, type PlainKey } from '../i18n/t.ts';
import { isListedLanguage, OTHER_LANGUAGE } from './languages.ts';
import type { Register } from './register.ts';

/** Mirrors the server's `CefrLevel` enum (schema.graphql), in its order. */
export const CEFR_LEVELS = ['PRE_A1', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

export type CefrLevel = (typeof CEFR_LEVELS)[number];

/**
 * Whether a string is a level this client knows.
 *
 * Relay's generated enums carry `"%future added value"` for a variant the
 * schema gained after these artifacts were built, and the server's own
 * `GqlCefr` pins its wire strings precisely so a rename upstream cannot
 * break stored levels. A level this build has never heard of is kept as
 * unknown, never mapped to a real option: see `formFor`.
 */
export function isCefrLevel(value: string | null | undefined): value is CefrLevel {
  return CEFR_LEVELS.includes(value as CefrLevel);
}

/**
 * A problem, and where it belongs on screen.
 *
 * `field` is null for a problem the form as a whole has — a server that
 * refused the request, or one that could not be reached. Design §4: those must
 * not be attached to a guessed field, because the message would then suggest
 * the learner typed something wrong.
 *
 * Messages carried a field implicitly before, in their wording and their
 * position in the returned list, which is why they could only ever be rendered
 * in one block above the submit button.
 */
export type FieldName = 'email' | 'password' | 'displayName' | 'nativeLanguage' | 'cefrLevel';
export type FieldError = { field: FieldName | null; message: string };

export type ProfileForm = {
  displayName: string;
  /** `''` (declined), a name from `NATIVE_LANGUAGES`, or `OTHER_LANGUAGE`. */
  languageChoice: string;
  /** Only meaningful when `languageChoice` is `OTHER_LANGUAGE`. */
  otherLanguage: string;
  /** `null` when the stored level is one this build cannot show. */
  cefrLevel: CefrLevel | null;
};

/**
 * What the signup screen collects: the credential and the profile together.
 *
 * One form, one request. A learner is created by signup now — `createLearner`
 * is gone from the schema — so splitting these would mean an account that
 * exists with no learner between two round trips.
 */
export type SignupForm = Omit<ProfileForm, 'cefrLevel'> & {
  email: string;
  password: string;
  cefrLevel: CefrLevel;
};

/** The `SignUpInput` the `signUp` mutation takes. It was a plain JSON body to
 * `POST /auth/signup`; the route is gone and credentials are in the schema
 * now — see the design's §5 amendment for why that reversed. */
export type SignupBody = {
  email: string;
  password: string;
  displayName: string;
  nativeLanguage?: string;
  cefrLevel: CefrLevel;
  displayLanguage: string;
};

/** The `UpdateLearnerInput` the `updateLearner` mutation takes. The name and
 * language every time: it is a replacement rather than a patch, so clearing the
 * language is distinguishable from leaving it alone. The level is the
 * exception: absent keeps it, and a change names the level it replaces. */
export type ProfileBody = {
  displayName: string;
  nativeLanguage: string | null;
  cefrLevel?: CefrLevel;
  previousCefrLevel?: CefrLevel;
};

/**
 * The server's own floor, mirrored so the form can say so before a round trip.
 * Length only: composition rules push people towards `Password1!` and buy
 * nothing that a slow hash does not already.
 */
export const MIN_PASSWORD_CHARS = 8;

/**
 * The display name reaches no prompt, but it does reach the debrief and the
 * UI, and an unbounded one is a layout bug waiting to happen. The server's own
 * bound on prompt-bound text is 40 chars for a language and 120 for a topic
 * (prompt.rs); 60 sits between them and is generous for a name.
 */
export const MAX_DISPLAY_NAME_CHARS = 60;

/**
 * Mirrors `MAX_LANGUAGE_CHARS` in crates/tutor-core/src/prompt.rs, which is
 * the original and which truncates past it in silence.
 *
 * A second copy of a number is a second source of truth, and this one is
 * deliberate: the alternative is what happens today, where an over-long
 * language is cut server-side and nothing ever tells the learner.
 */
export const MAX_LANGUAGE_CHARS = 40;

/**
 * Whether a free-typed native language reads as English.
 *
 * Mirrors `tutor_core::language::is_english`'s ordinary spellings, pinned by
 * that module's own `is_english_recognises_every_spelling_display_name_does`
 * test -- not a port of its full resolution (isolang's alias table, 639-1/
 * 639-3 codes, `Language::from_name` and its lowercase fallback), which has no
 * client-side equivalent and stays the one source of truth. Only the "Other…"
 * box can reach this: `NATIVE_LANGUAGES` excludes English by design
 * (`languages.ts`'s own comment), so nothing in the dropdown can produce it.
 * A spelling this misses still refuses server-side; `aidErrors.ts`'s mapping
 * is what makes that refusal legible instead of "Could not reach the tutor."
 */
export function looksEnglish(language: string): boolean {
  const normalized = language.trim().toLowerCase();
  return normalized === 'english' || normalized === 'en' || normalized === 'eng';
}

/**
 * What each CEFR band means, for the learner who does not know the scale.
 *
 * Keyed by `CefrLevel` rather than written as a list, so adding a level to
 * `CEFR_LEVELS` without describing it is a type error rather than a blank
 * option on the form.
 */
export const LEVEL_DESCRIPTIONS: Record<CefrLevel, PlainKey> = {
  PRE_A1: 'level-pre-a1',
  A1: 'level-a1',
  A2: 'level-a2',
  B1: 'level-b1',
  B2: 'level-b2',
  C1: 'level-c1',
  C2: 'level-c2',
};

/**
 * The code first for the learner who tracks it, the meaning after for the one
 * who does not. Bare codes let a learner who does not know the scale take the
 * default, and the tutor then calibrates against a level nobody asserted.
 */
export function levelLabel(level: CefrLevel, register: Register): string {
  if (register === 'kid') return t(KID_LEVEL_LABELS[level]);
  return t('level-adult', { code: LEVEL_CODES[level], description: t(LEVEL_DESCRIPTIONS[level]) });
}

/**
 * The same levels, for the parent who reads the kid register (pre-A1 §4).
 *
 * The three young-learner bands lead with the Cambridge names a parent has
 * heard; the rest keep the code first. Third person, because a child in kid
 * mode reads English poorly or not at all, so the reader here is the parent.
 * A `Record` for `LEVEL_DESCRIPTIONS`' reason: a level with no kid label is a
 * type error rather than a blank option.
 */
export const KID_LEVEL_LABELS: Record<CefrLevel, PlainKey> = {
  PRE_A1: 'level-kid-pre-a1',
  A1: 'level-kid-a1',
  A2: 'level-kid-a2',
  B1: 'level-kid-b1',
  B2: 'level-kid-b2',
  C1: 'level-kid-c1',
  C2: 'level-kid-c2',
};

/**
 * The code a person reads. Not the wire value, since GraphQL cannot spell
 * "Pre-A1" (pre-A1 §3).
 */
export const LEVEL_CODES: Record<CefrLevel, string> = {
  PRE_A1: 'Pre-A1',
  A1: 'A1',
  A2: 'A2',
  B1: 'B1',
  B2: 'B2',
  C1: 'C1',
  C2: 'C2',
};

/**
 * The one string the server stores, whichever control produced it.
 *
 * Two pieces of state collapse to one here because "picked Other and typed
 * nothing" and "declined to answer" have to be distinguishable while the form
 * is being filled in, and are the same thing once it is submitted.
 */
export function nativeLanguageOf(form: ProfileForm): string {
  if (form.languageChoice === OTHER_LANGUAGE) return form.otherLanguage.trim();
  return form.languageChoice.trim();
}

/**
 * What is wrong with a password alone.
 *
 * Separate from `validateCredentials` because the reset screen sets a password
 * with no address in front of it — there is nothing to validate an address
 * against, and inventing a placeholder to satisfy a signature is how a
 * placeholder ends up in a request.
 */
export function validatePassword(password: string): FieldError[] {
  return password.length < MIN_PASSWORD_CHARS
    ? [{ field: 'password', message: t('field-password-short', { min: MIN_PASSWORD_CHARS }) }]
    : [];
}

/**
 * Human-readable problems with the credential half, in display order.
 *
 * Deliberately permissive about the address: the only check is the one the
 * server makes, because anything stricter rejects addresses that work. What
 * proves an address is a message sent to it, which is the mailer's job.
 */
export function validateCredentials(form: { email: string; password: string }): FieldError[] {
  const errors: FieldError[] = [];
  const email = form.email.trim();

  if (email.length === 0) {
    errors.push({ field: 'email', message: t('field-email-empty') });
  } else if (!email.includes('@') || email.startsWith('@') || email.endsWith('@')) {
    errors.push({ field: 'email', message: t('field-email-invalid') });
  }
  return [...errors, ...validatePassword(form.password)];
}

/** Everything wrong with a signup form: the credential, then the profile. */
export function validateSignup(form: SignupForm): FieldError[] {
  return [...validateCredentials(form), ...validateProfile(form)];
}

/** Human-readable problems, in display order. Empty means the form is valid. */
export function validateProfile(form: ProfileForm): FieldError[] {
  const errors: FieldError[] = [];
  const name = form.displayName.trim();

  if (name.length === 0) {
    errors.push({ field: 'displayName', message: t('field-name-empty') });
  } else if (name.length > MAX_DISPLAY_NAME_CHARS) {
    errors.push({
      field: 'displayName',
      message: t('field-name-long', { max: MAX_DISPLAY_NAME_CHARS }),
    });
  }

  // The language stays optional -- the select's empty option is the way to
  // decline -- but Other is an affirmative choice, and an unfinished one must
  // not quietly resolve to the same thing as declining.
  const language = nativeLanguageOf(form);
  if (form.languageChoice === OTHER_LANGUAGE) {
    if (language.length === 0) {
      errors.push({ field: 'nativeLanguage', message: t('field-language-empty') });
    } else if (language.length > MAX_LANGUAGE_CHARS) {
      errors.push({ field: 'nativeLanguage', message: t('field-language-long', { max: MAX_LANGUAGE_CHARS }) });
    } else if (looksEnglish(language)) {
      // Ruling Y, mirrored: refused at the door rather than round-tripped to
      // the server only to bounce back as a refusal the learner cannot read.
      errors.push({
        field: 'nativeLanguage',
        message: t('field-language-english'),
      });
    }
  } else if (language.length > 0 && !isListedLanguage(language)) {
    // Unreachable through the select. Reachable through a replayed request,
    // or a stored value from before this list carried that name.
    errors.push({ field: 'nativeLanguage', message: t('field-language-unlisted') });
  }

  return errors;
}

/**
 * The mutation payload for a form that has already passed `validateProfile`.
 *
 * A blank native language is omitted rather than sent as `""`: the column is
 * nullable, and "" would store a value that means the same as absent while
 * reading, to anyone querying the table, as though the learner had answered.
 *
 * `displayLanguage` is the locale the form is shown in: the verification
 * letter is written in it (native-language UI spec §8, phase 3b).
 */
export function signupBody(form: SignupForm, displayLanguage: string): SignupBody {
  const nativeLanguage = nativeLanguageOf(form);
  return {
    // Trimmed here as well as on the server: what the learner sees echoed back
    // should be what was stored, not what they typed.
    email: form.email.trim(),
    password: form.password,
    displayName: form.displayName.trim(),
    cefrLevel: form.cefrLevel,
    displayLanguage,
    ...(nativeLanguage.length > 0 ? { nativeLanguage } : {}),
  };
}

/**
 * The same profile, for `updateLearner` rather than `signUp`.
 *
 * `null` where `signupBody` omits: this one replaces the language, so an
 * absent language would mean "leave it alone" and there would be no way to
 * clear one. Signup has nothing to clear.
 *
 * `previous` is the level the server last said was stored. The server writes a
 * changed level only while that is still true, so a form opened before
 * someone else changed the level cannot quietly undo them.
 */
export function profileBody(form: ProfileForm, previous: CefrLevel | null): ProfileBody {
  const nativeLanguage = nativeLanguageOf(form);
  return {
    displayName: form.displayName.trim(),
    nativeLanguage: nativeLanguage.length > 0 ? nativeLanguage : null,
    ...(form.cefrLevel !== null ? { cefrLevel: form.cefrLevel } : {}),
    ...(form.cefrLevel !== null && previous !== null ? { previousCefrLevel: previous } : {}),
  };
}

/**
 * The profile form for a stored learner.
 *
 * A stored language the list does not carry opens on Other with the value in
 * the box. Not a defensive nicety: the list is a product call that will be
 * edited, and a learner whose language was dropped from it must not have it
 * silently replaced by "prefer not to say" the next time they change their
 * level.
 *
 * A stored level this build does not know opens on `null`, never on a real
 * option. Falling back to B1 here is what the pre-A1 spec's rollout risk was:
 * a tab from before Pre-A1 showed a child as B1, and Save made it true.
 */
export function formFor(
  learner: { displayName: string; nativeLanguage?: string | null; cefrLevel: string } | null | undefined,
): ProfileForm {
  const stored = learner?.nativeLanguage ?? '';
  const listed = stored !== '' && isListedLanguage(stored);
  return {
    displayName: learner?.displayName ?? '',
    languageChoice: stored === '' ? '' : listed ? stored : OTHER_LANGUAGE,
    otherLanguage: listed ? '' : stored,
    cefrLevel: !learner ? 'B1' : isCefrLevel(learner.cefrLevel) ? learner.cefrLevel : null,
  };
}
